/**
 * Operations → Waiver — Encore / External email body assembly + draft storage.
 * Mailto only (no attachments). Templates are plain-text (ignore Word bold).
 */

export const WAIVER_TYPES = [
  { value: 'encore', label: 'Encore' },
  { value: 'external', label: 'External' },
]

const DRAFT_KEY = 'encore.waiver.draft'

export const DEFAULT_ENCORE_CREDIT_PHRASE =
  ', including as a primary artist on product level'

/** Suffix only — Side Artist is prepended when the courtesy section is on. */
export const DEFAULT_ENCORE_COURTESY_CREDIT =
  ' appears courtesy of Encore Recordings'

export const DEFAULT_EXTERNAL_CREDIT_PHRASE = 'on the product level'

export function courtesyCreditDefault() {
  return DEFAULT_ENCORE_COURTESY_CREDIT
}

/** Preserve leading space on the courtesy suffix (clean() would trim it). */
function courtesyCreditSuffix(raw) {
  const s = String(raw ?? '').replace(/\u00a0/g, ' ')
  if (!s.trim() || s.includes('{')) return DEFAULT_ENCORE_COURTESY_CREDIT
  // Old drafts stored the full line ("Guest appears courtesy…") — use suffix only.
  const trimmed = s.trim()
  if (
    /appears courtesy of Encore/i.test(trimmed) &&
    !trimmed.toLowerCase().startsWith('appears courtesy')
  ) {
    return DEFAULT_ENCORE_COURTESY_CREDIT
  }
  return s
}

/** Side Artist + courtesy suffix (field is the trailing phrase). */
export function composeCourtesyCredit(raw, sideArtist) {
  const side = clean(sideArtist)
  const suffix = courtesyCreditSuffix(raw)
  if (!side) return suffix.trim()
  if (suffix.startsWith(' ') || suffix.startsWith(',') || suffix.startsWith('.')) {
    return `${side}${suffix}`
  }
  return `${side} ${suffix}`
}

const ONES = [
  'zero',
  'one',
  'two',
  'three',
  'four',
  'five',
  'six',
  'seven',
  'eight',
  'nine',
  'ten',
  'eleven',
  'twelve',
  'thirteen',
  'fourteen',
  'fifteen',
  'sixteen',
  'seventeen',
  'eighteen',
  'nineteen',
]

const TENS = [
  '',
  '',
  'twenty',
  'thirty',
  'forty',
  'fifty',
  'sixty',
  'seventy',
  'eighty',
  'ninety',
]

function clean(value) {
  return String(value ?? '')
    .replace(/\u00a0/g, ' ')
    .trim()
}

/** Normalize free-text / ISO dates for ``<input type="date">`` (YYYY-MM-DD). */
export function toDateInputValue(raw) {
  const s = clean(raw)
  if (!s) return ''
  const iso = s.match(/^(\d{4})-(\d{2})-(\d{2})/)
  if (iso) return `${iso[1]}-${iso[2]}-${iso[3]}`
  const us = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{2}|\d{4})$/)
  if (us) {
    let year = Number(us[3])
    if (us[3].length === 2) year = 2000 + year
    const mm = String(Number(us[1])).padStart(2, '0')
    const dd = String(Number(us[2])).padStart(2, '0')
    return `${year}-${mm}-${dd}`
  }
  return ''
}

/** Format stored date for email body (e.g. January 15, 2027). */
export function formatDateForWaiverBody(raw) {
  const iso = toDateInputValue(raw)
  if (!iso) return clean(raw)
  const [y, m, d] = iso.split('-').map(Number)
  if (!y || !m || !d) return clean(raw)
  const dt = new Date(Date.UTC(y, m - 1, d))
  if (Number.isNaN(dt.getTime())) return clean(raw)
  return dt.toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  })
}

/** Parse 0–100 percent points from "50", "50%", etc. */
export function parseRoyaltyPercent(raw) {
  const s = clean(raw).replace(/%/g, '')
  if (!s) return null
  const n = Number(s)
  if (!Number.isFinite(n)) return null
  const rounded = Math.round(n)
  if (rounded < 0 || rounded > 100) return null
  return rounded
}

function spellInteger0to100(n) {
  if (n <= 19) return ONES[n]
  if (n === 100) return 'one hundred'
  const tens = Math.floor(n / 10)
  const ones = n % 10
  if (ones === 0) return TENS[tens]
  return `${TENS[tens]}-${ONES[ones]}`
}

/** 50 → "fifty percent (50%)" */
export function formatRoyaltyPercent(raw) {
  const n = parseRoyaltyPercent(raw)
  if (n == null) return ''
  return `${spellInteger0to100(n)} percent (${n}%)`
}

export function defaultEncoreOptions() {
  return {
    includeAlbumRights: true,
    includeTrackRights: true,
    includeRemixes: false,
    includeVideoDate: false,
    includeCourtesyCredit: true,
  }
}

export function defaultExternalOptions() {
  return {
    includeAlbumRights: true,
    includeSingle: true,
    includeCredit: true,
    includeCreditPhrase: true,
  }
}

export function defaultEncoreValues() {
  return {
    SideArtist: '',
    ExternalPrimaryArtist: '',
    SongTitle: '',
    ExternalLabelDistributor: '',
    NumRemixes: '',
    AlbumReleaseDate: '',
    TrackReleaseDate: '',
    VideoDate: '',
    // Optional suffix; suggestion is placeholder-only (see field meta).
    CreditPhrase: '',
    CourtesyCredit: DEFAULT_ENCORE_COURTESY_CREDIT,
    SideArtistRoyalty: '50',
    RecipientEmail: '',
  }
}

export function defaultExternalValues() {
  return {
    ContactName: '',
    ExternalLabelDistributor: '',
    ExternalSideArtist: '',
    SongTitle: '',
    EncorePrimaryArtist: '',
    AlbumReleaseDate: '',
    SingleReleaseDate: '',
    VideoDate: '',
    ExternalSideArtistRoyalty: '50',
    CreditPhrase: DEFAULT_EXTERNAL_CREDIT_PHRASE,
    MasterUrl: '',
    Pass: '',
    ApprovalDeadline: '',
    RecipientEmail: '',
  }
}

export function encoreFieldMeta() {
  return [
    { name: 'RecipientEmail', label: 'Email Recipient', required: false },
    { name: 'SideArtist', label: 'Side Artist (Encore)', required: true },
    { name: 'ExternalPrimaryArtist', label: 'Primary Artist (External)', required: true },
    { name: 'SongTitle', label: 'Song Title', required: true },
    { name: 'ExternalLabelDistributor', label: 'External Label / Distributor', required: true },
    { name: 'NumRemixes', label: 'Number of Remixes', required: false, option: 'includeRemixes' },
    {
      name: 'AlbumReleaseDate',
      label: 'Album Release Date',
      required: false,
      option: 'includeAlbumRights',
      inputType: 'date',
    },
    {
      name: 'TrackReleaseDate',
      label: 'Track Release Date',
      required: false,
      option: 'includeTrackRights',
      inputType: 'date',
    },
    {
      name: 'VideoDate',
      label: 'Video Date',
      required: false,
      option: 'includeVideoDate',
      inputType: 'date',
    },
    { name: 'SideArtistRoyalty', label: 'Encore Side Artist Royalty (%)', required: true },
    {
      name: 'CreditPhrase',
      label: 'Primary Artist Credit Phrase (optional)',
      required: false,
      placeholder: DEFAULT_ENCORE_CREDIT_PHRASE,
      placeholderStyle: 'suggestion',
    },
    {
      name: 'CourtesyCredit',
      label: 'Courtesy Credit',
      required: true,
      option: 'includeCourtesyCredit',
    },
  ]
}

export function externalFieldMeta() {
  return [
    { name: 'RecipientEmail', label: 'Email Recipient', required: false },
    { name: 'ContactName', label: 'Contact Name', required: true },
    { name: 'ExternalLabelDistributor', label: 'External Label / Distributor', required: true },
    { name: 'ExternalSideArtist', label: 'Side Artist (External)', required: true },
    { name: 'SongTitle', label: 'Song Title', required: true },
    { name: 'EncorePrimaryArtist', label: 'Primary Artist (Encore)', required: true },
    {
      name: 'AlbumReleaseDate',
      label: 'Album Release Date',
      required: false,
      option: 'includeAlbumRights',
      inputType: 'date',
    },
    {
      name: 'SingleReleaseDate',
      label: 'Single Release Date',
      required: false,
      option: 'includeSingle',
      inputType: 'date',
    },
    {
      name: 'VideoDate',
      label: 'Video Release Date',
      required: true,
      inputType: 'date',
    },
    { name: 'ExternalSideArtistRoyalty', label: 'External Side Artist Royalty (%)', required: true },
    { name: 'MasterUrl', label: 'Master Link', required: false },
    { name: 'Pass', label: 'Pass', required: false },
    {
      name: 'CreditPhrase',
      label: 'Primary Artist Credit Phrase',
      required: false,
      option: 'includeCreditPhrase',
    },
    {
      name: 'ApprovalDeadline',
      label: 'Approval Deadline',
      required: true,
      inputType: 'date',
    },
  ]
}

export function visibleFields(waiverType, options) {
  const meta = waiverType === 'external' ? externalFieldMeta() : encoreFieldMeta()
  const opts = options || {}
  return meta.filter((f) => {
    if (!f.option) return true
    // Credit phrase only when credit section is on (External) or phrase toggle (Encore)
    if (f.name === 'CreditPhrase' && waiverType === 'external') {
      return Boolean(opts.includeCredit) && Boolean(opts.includeCreditPhrase)
    }
    return Boolean(opts[f.option])
  })
}

export function missingRequiredFields(waiverType, values, options) {
  return visibleFields(waiverType, options).filter((f) => {
    if (f.required === false) return false
    if (f.name === 'SideArtistRoyalty' || f.name === 'ExternalSideArtistRoyalty') {
      return parseRoyaltyPercent(values?.[f.name]) == null
    }
    return !clean(values?.[f.name])
  })
}

export function defaultSubject(waiverType, values) {
  const title = clean(values?.SongTitle) || 'Song'
  if (waiverType === 'external') {
    return `Side artist clearance request — ${title}`
  }
  return `Encore exclusivity waiver — ${title}`
}

/**
 * Build Encore waiver email body from values + optional toggles.
 */
export function buildEncoreBody(values = {}, options = {}) {
  const v = { ...defaultEncoreValues(), ...values }
  const o = { ...defaultEncoreOptions(), ...options }

  const side = clean(v.SideArtist)
  const primary = clean(v.ExternalPrimaryArtist)
  const song = clean(v.SongTitle)
  const label = clean(v.ExternalLabelDistributor)
  const courtesy = composeCourtesyCredit(v.CourtesyCredit, side)
  const royalty = formatRoyaltyPercent(v.SideArtistRoyalty)

  let remixClause = ''
  if (o.includeRemixes && clean(v.NumRemixes)) {
    remixClause = ` along with ${clean(v.NumRemixes)} remixes of the track simultaneously with the original`
  }

  const open = `This correspondence shall confirm that Encore Recordings, LLC ("Encore") agrees to waive its exclusivity with respect to ${side} ("Side Artist") performance on the ${primary} ("Artist") song "${song}",${remixClause} (individually and collectively the "Master") to be released by ${label} ("you") on the dates noted below for the following rights:`

  const bullets = []
  if (o.includeAlbumRights) {
    const d = formatDateForWaiverBody(v.AlbumReleaseDate)
    bullets.push(d ? `• Album rights (release date ${d})` : '• Album rights')
  }
  if (o.includeTrackRights) {
    const d = formatDateForWaiverBody(v.TrackReleaseDate)
    bullets.push(
      d ? `• Standalone track rights (release date ${d})` : '• Standalone track rights',
    )
  }

  // Credit bullet always present; optional phrase only when the user typed something.
  let creditPhrase = ''
  if (clean(v.CreditPhrase)) {
    creditPhrase = clean(v.CreditPhrase)
    if (!creditPhrase.startsWith(',') && !creditPhrase.startsWith(' ')) {
      creditPhrase = ` ${creditPhrase}`
    }
  }
  bullets.push(
    `• Credit - Use of Side Artist's name in a featuring credit${creditPhrase}`,
  )

  let videoLine =
    "• Video - (subject to Side Artist's approval over any video"
  if (o.includeVideoDate && clean(v.VideoDate)) {
    videoLine += `, date ${formatDateForWaiverBody(v.VideoDate)}`
  }
  videoLine += ')'
  bullets.push(videoLine)

  if (o.includeCourtesyCredit) {
    const courtesyClause = courtesy
      ? `• Courtesy Credit - Encore must be granted a courtesy credit in the following form: ${courtesy}`
      : '• Courtesy Credit - Encore must be granted a courtesy credit'
    bullets.push(courtesyClause)
  }
  bullets.push(
    "• Use of Side Artist's name in connection with marketing and promotion, including stickers (provided Side Artist's name shall be included with at least three (3) other guest artists of same size and prominence)",
  )

  const consideration = `In consideration of the foregoing rights, Encore shall receive a royalty of ${royalty} of the net artist royalty, payable only after recoupment of actual recording costs of the Master (excluding in-pocket advances to the Artist and Side Artist), which shall be accounted to Encore semi-annually.`

  const except = `Except for the uses specifically referenced above, you will not have the right to use or otherwise exploit the Master or Side Artist's name or likeness in any manner or medium (including, without limitation in remixes other than those approved herein, compilation albums greatest hits albums, synchronization in motion pictures or television productions, sample licenses, etc.) without Encore's prior written consent, which may be withheld at Encore's sole discretion.`

  const condition = `Please note that Encore's waiver as set forth herein shall be conditioned on you obtaining Side Artist's consent in respect of the exploitation of Side Artist's performances and name and/or likeness as provided herein. To the extent that the agreement between the artists contains more restrictive terms or is more favorable for Encore/Side Artist, such terms shall apply to Encore's waiver.`

  const payment =
    'Please see the attached payment instructions and Encore W-9 for purposes of royalty payments due in connection with the waiver.'

  const tawny = 'Also, looping in Tawny from our finance team for payment set up/collection.'

  return [
    open,
    '',
    ...bullets,
    '',
    consideration,
    '',
    except,
    '',
    condition,
    '',
    payment,
    '',
    tawny,
    '',
    'Best,',
  ].join('\n')
}

/**
 * Build External waiver email body; optional album/single/credit clauses renumber.
 */
export function buildExternalBody(values = {}, options = {}) {
  const v = { ...defaultExternalValues(), ...values }
  const o = { ...defaultExternalOptions(), ...options }

  const contact = clean(v.ContactName)
  const label = clean(v.ExternalLabelDistributor)
  const guest = clean(v.ExternalSideArtist)
  const song = clean(v.SongTitle)
  const artist = clean(v.EncorePrimaryArtist)
  const royalty = formatRoyaltyPercent(v.ExternalSideArtistRoyalty)
  const videoDate = formatDateForWaiverBody(v.VideoDate)
  const deadline = formatDateForWaiverBody(v.ApprovalDeadline)
  const masterUrl = clean(v.MasterUrl)
  const pass = clean(v.Pass)

  const greeting = [
    contact ? `Hi ${contact},` : 'Hi,',
    '',
    'Hope all is well.',
    '',
    `Encore Recordings ("Encore") would like to formally request side artist clearance for ${label}'s ("you" or "your") exclusive recording artist p/k/a "${guest}" ("Guest Artist") whose performance is included on the recording entitled "${song}" ("Master") by Encore's exclusive recording artist professionally known as "${artist}" ("Artist") for release, subject to the terms and conditions below:`,
  ].join('\n')

  const clauses = []
  clauses.push({
    key: 'ownership',
    text: 'Ownership – Encore will own the Master and may exploit it throughout the world in perpetuity.',
  })

  if (o.includeAlbumRights) {
    const d = formatDateForWaiverBody(v.AlbumReleaseDate)
    const dateBit = d ? ` (Release Date: ${d})` : ''
    clauses.push({
      key: 'album',
      text: `Album Rights – One (1) use of the Master on Artist's forthcoming studio album, including so called "unbundled" album download rights and streaming rights${dateBit}; and one (1) use of the Master on Artist's "best of" or "greatest hits" album.`,
    })
  }

  if (o.includeSingle) {
    const d = formatDateForWaiverBody(v.SingleReleaseDate)
    const dateBit = d ? ` (Release Date: ${d})` : ''
    clauses.push({
      key: 'single',
      text: `Single – Encore will have the right to use the Master as a promotional and/or commercial single${dateBit}`,
    })
  }

  clauses.push({
    key: 'video',
    text: videoDate
      ? `Video – one (1) video embodying the Master (Release Date: ${videoDate}).`
      : 'Video – one (1) video embodying the Master.',
  })

  clauses.push({
    key: 'royalty',
    text: `Royalty – ${royalty} of the net Artist royalty, payable to you on all sales and exploitations of the Master throughout the world after recoupment of the recording costs for the Master only at the net artist rate (excluding any in-pocket advances or payments made to Artist) and otherwise on the same basis as royalties are payable by Encore to the Artist, and prorated for album sales.`,
  })

  if (o.includeCredit) {
    let phrase = ''
    if (o.includeCreditPhrase && clean(v.CreditPhrase)) {
      phrase = ` ${clean(v.CreditPhrase)}`
    }
    clauses.push({
      key: 'credit',
      text: `Credit – Encore shall provide the following credit: "${guest} appears courtesy of ${label}" (or as otherwise requested by you). Guest Artist to receive a primary artist credit${phrase}.`,
    })
  }

  clauses.push({
    key: 'name',
    text: "Name & Likeness – Encore may use Guest Artist's name and likeness in promo & ads in connection with the Master, Single, Video and the Album, including on stickers (provided that with respect to the Album, at least three (3) other artists' name shall be used in the same font, size, color, etc.). No use shall misrepresent the extent of Guest Artist's involvement in any way.",
  })

  const numbered = clauses.map((c, i) => `${i + 1}. ${c.text}`)

  const links = [
    'The Master is available for review here:',
    '',
    `Master: ${masterUrl || ''}`,
    `Pass: ${pass || ''}`,
  ]

  const close = deadline
    ? `Please respond at your earliest, as we need approval by ${deadline} to make delivery.`
    : 'Please respond at your earliest, as we need approval to make delivery.'

  return [greeting, '', ...numbered, '', ...links, '', close].join('\n')
}

export function buildWaiverBody(waiverType, values, options) {
  if (waiverType === 'external') return buildExternalBody(values, options)
  return buildEncoreBody(values, options)
}

/** Practical ceiling for mailto URLs (browser / Outlook); above this, copy body. */
export const MAILTO_SAFE_LENGTH = 1800

export function buildWaiverMailto({ recipient, subject, body }) {
  const qs = `subject=${encodeURIComponent(subject || '')}&body=${encodeURIComponent(body || '')}`
  const to = clean(recipient)
  if (to) return `mailto:${encodeURIComponent(to)}?${qs}`
  return `mailto:?${qs}`
}

export function mailtoExceedsSafeLength(href) {
  return String(href || '').length > MAILTO_SAFE_LENGTH
}

/**
 * Open a mailto draft. When the URL is too long for the client, copy the body
 * and open mailto with subject (+ To) only so the user can paste.
 * Returns ``{ opened, copiedBody, href }``.
 */
export async function openWaiverMailto({ recipient, subject, body }) {
  const fullHref = buildWaiverMailto({ recipient, subject, body })
  if (!mailtoExceedsSafeLength(fullHref)) {
    return { opened: fullHref, copiedBody: false, href: fullHref }
  }
  let copiedBody = false
  try {
    if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(String(body || ''))
      copiedBody = true
    }
  } catch {
    copiedBody = false
  }
  const shortHref = buildWaiverMailto({ recipient, subject, body: '' })
  return { opened: shortHref, copiedBody, href: fullHref, truncated: true }
}

export function loadWaiverDraft() {
  try {
    const raw = window.sessionStorage?.getItem(DRAFT_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw)
    if (!parsed || typeof parsed !== 'object') return null
    return parsed
  } catch {
    return null
  }
}

export function saveWaiverDraft(draft) {
  try {
    window.sessionStorage?.setItem(DRAFT_KEY, JSON.stringify(draft))
  } catch {
    /* ignore */
  }
}

export function clearWaiverDraft() {
  try {
    window.sessionStorage?.removeItem(DRAFT_KEY)
  } catch {
    /* ignore */
  }
}
